import React from "react";
import { Check, Copy, RefreshCw, Webhook } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { generateWebhookToken, type FluxoSettings } from "@/hooks/useFluxos";

export const buildWebhookUrl = (token: string): string => {
  const base = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.replace(/\/$/, "");
  if (base) {
    return `${base}/functions/v1/fluxo-disparar/${token}`;
  }
  // Fallback de documentação quando o env não está disponível em build.
  return `https://api.rolt.app/fluxos/${token}/disparar`;
};

const buildCurlExample = (token: string): string =>
  `curl -X POST \\
  ${buildWebhookUrl(token)} \\
  -H "Content-Type: application/json" \\
  -d '{
    "telefone": "5511999999999",
    "variaveis": {
      "nome": "Maria Silva",
      "primeiro_nome": "Maria",
      "email": "maria@email.com"
    }
  }'`;

interface CopyButtonProps {
  value: string;
  successMessage: string;
  ariaLabel: string;
}

const CopyButton: React.FC<CopyButtonProps> = ({ value, successMessage, ariaLabel }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(successMessage);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar. Copie manualmente.");
    }
  };

  return (
    <button
      onClick={handleCopy}
      aria-label={ariaLabel}
      className="shrink-0 w-[34px] h-[34px] flex justify-center items-center border border-border bg-white hover:bg-[#F1F5F9] rounded-[10px] transition-colors dark:border-[#3F3F464D] dark:bg-neutral-600 dark:hover:bg-neutral-500 dark:rounded-[8px]"
    >
      {copied ? (
        <Check size={15} className="text-green-500 dark:text-green-400" />
      ) : (
        <Copy size={15} className="text-[#64748B] dark:text-neutral-50" />
      )}
    </button>
  );
};

interface FluxoSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settings: FluxoSettings;
  onSettingsChange: (settings: FluxoSettings) => void;
}

const FluxoSettingsModal: React.FC<FluxoSettingsModalProps> = ({
  open,
  onOpenChange,
  settings,
  onSettingsChange,
}) => {
  const webhookUrl = buildWebhookUrl(settings.webhookToken);

  const handleToggle = (checked: boolean) => {
    onSettingsChange({
      ...settings,
      webhookEnabled: checked,
      webhookToken: settings.webhookToken || generateWebhookToken(),
    });
  };

  const handleRegenerateToken = () => {
    if (
      !confirm(
        "Gerar uma nova URL? A URL atual deixará de funcionar e você precisará atualizar os sistemas que a utilizam."
      )
    ) {
      return;
    }
    onSettingsChange({ ...settings, webhookToken: generateWebhookToken() });
    toast.success("Nova URL gerada");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex w-full p-0 border-border bg-white flex-col max-w-[560px] dark:border-[#3F3F464D] dark:bg-[#18181B]">
        <div className="max-h-[90dvh] p-5 overflow-y-auto flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A] dark:text-neutral-50">
              Configurações avançadas
            </DialogTitle>
            <DialogDescription className="text-[#64748B] dark:text-neutral-400">
              Defina como este fluxo pode ser acionado.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3 p-4 bg-[#F8FAFC] border border-border rounded-lg dark:bg-[#27272A] dark:border-[#3F3F464D]">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 shrink-0 bg-[#EFF6FF] rounded-[10px] flex items-center justify-center dark:bg-neutral-600 dark:rounded-[8px]">
                  <Webhook size={18} className="text-[#2563EB] dark:text-neutral-50" />
                </div>
                <div>
                  <Label className="text-[#0F172A] font-medium dark:text-neutral-50">
                    Acionar via webhook
                  </Label>
                  <p className="text-xs text-[#64748B] mt-1 dark:text-neutral-400">
                    Permite que sistemas externos disparem a sequência deste fluxo para um
                    contato enviando uma requisição HTTP.
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.webhookEnabled}
                onCheckedChange={handleToggle}
                activeColor="data-[state=checked]:bg-[#16A34A] dark:data-[state=checked]:bg-[#22C55E]"
                className="mt-1 shrink-0"
              />
            </div>

            {settings.webhookEnabled && (
              <div className="flex flex-col gap-4 pt-3 border-t border-border dark:border-[#3F3F464D]">
                <div className="flex flex-col gap-2">
                  <Label className="text-[#0F172A] text-sm dark:text-neutral-50">URL do webhook</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0 px-3 py-2 bg-white border border-border rounded-[8px] dark:bg-neutral-600 dark:border-[#3F3F46]">
                      <p className="text-[13px] font-mono text-[#0F172A] truncate dark:text-neutral-50">
                        {webhookUrl}
                      </p>
                    </div>
                    <CopyButton
                      value={webhookUrl}
                      successMessage="URL copiada"
                      ariaLabel="Copiar URL do webhook"
                    />
                  </div>
                  <button
                    onClick={handleRegenerateToken}
                    className="self-start flex items-center gap-1.5 text-xs text-[#64748B] hover:text-[#0F172A] transition-colors dark:text-neutral-400 dark:hover:text-neutral-200"
                  >
                    <RefreshCw size={12} />
                    Gerar nova URL
                  </button>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-[#0F172A] text-sm dark:text-neutral-50">
                      Exemplo de requisição
                    </Label>
                    <CopyButton
                      value={buildCurlExample(settings.webhookToken)}
                      successMessage="Exemplo copiado"
                      ariaLabel="Copiar exemplo de requisição"
                    />
                  </div>
                  <pre className="p-3 bg-[#F1F5F9] border border-border rounded-[8px] overflow-x-auto text-[12px] leading-relaxed font-mono text-[#0F172A] whitespace-pre dark:bg-neutral-900 dark:border-[#3F3F46] dark:text-neutral-200">
                    {buildCurlExample(settings.webhookToken)}
                  </pre>
                  <p className="text-xs text-[#64748B] dark:text-neutral-400">
                    Envie um POST com o{" "}
                    <span className="font-mono text-[#334155] dark:text-neutral-300">telefone</span>{" "}
                    do contato e, opcionalmente, as variáveis usadas nos blocos do fluxo (
                    <span className="font-mono text-[#334155] dark:text-neutral-300">
                      {"{{ nome }}"}
                    </span>
                    ,{" "}
                    <span className="font-mono text-[#334155] dark:text-neutral-300">
                      {"{{ email }}"}
                    </span>{" "}
                    etc.).
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FluxoSettingsModal;
